import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Calculator, Save, Loader2 } from 'lucide-react';
import { estimatesApi, proceduresApi, locationsApi, costSectionsApi, roomTypesApi, icuRatesApi, pricingApi, doctorsApi } from '@/api/client';
import { formatCurrency } from '@medical-counsellor/shared';

const estimateSchema = z.object({
    procedureConfigId: z.string().min(1, 'Please select a procedure'),
    locationId: z.string().min(1, 'Please select a location'),
    patientName: z.string().min(1, 'Patient name is required'),
    patientId: z.string().optional(),
    stayDays: z.number().int().positive().min(1, 'Stay days must be at least 1'),
    roomType: z.string().min(1, 'Please select a room type'),
    icuDays: z.number().int().nonnegative().default(0),
    icuRateId: z.string().optional(),
    selectedConditions: z.array(z.string()).optional(),
    selectedItems: z.array(z.string()).optional(),
    itemQuantities: z.record(z.string(), z.number()).optional(),
    doctors: z.array(z.object({
        id: z.string(),
        isPrimary: z.boolean(),
    })).min(1, 'Please select a treating doctor'),
}).refine((data) => (data.icuDays || 0) <= data.stayDays, {
    message: 'ICU days cannot exceed total stay days',
    path: ['icuDays'],
});

type EstimateFormData = z.infer<typeof estimateSchema>;



export function NewEstimatePage() {
    const navigate = useNavigate();
    const [selectedLocationId, setSelectedLocationId] = useState('');
    const [selectedProcedureId, setSelectedProcedureId] = useState('');
    const [preview, setPreview] = useState<any>(null);
    const [isCalculating, setIsCalculating] = useState(false);

    const { data: locations = [] } = useQuery({
        queryKey: ['locations'],
        queryFn: locationsApi.list,
    });

    const { data: procedures = [], isLoading: proceduresLoading } = useQuery({
        queryKey: ['procedures'],
        queryFn: () => proceduresApi.list(),
    });

    const { data: sections = [], isLoading: sectionsLoading } = useQuery({
        queryKey: ['cost-sections', selectedLocationId],
        queryFn: () => costSectionsApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: roomTypes = [] } = useQuery({
        queryKey: ['room-types', selectedLocationId],
        queryFn: () => roomTypesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: icuRates = [] } = useQuery({
        queryKey: ['icu-rates', selectedLocationId],
        queryFn: () => icuRatesApi.list(selectedLocationId),
        enabled: !!selectedLocationId,
    });

    const { data: doctors = [] } = useQuery({
        queryKey: ['doctors', selectedLocationId],
        queryFn: () => doctorsApi.list(),
        select: (data) => data.filter((d: any) =>
            d.status === 'ACTIVE' &&
            (!selectedLocationId || d.locationIds?.includes(selectedLocationId))
        ),
    });

    const { data: procedureConfigId } = useQuery({
        queryKey: ['procedure-config-id', selectedProcedureId, selectedLocationId],
        queryFn: () => {
            const procedure = procedures.find((p: any) => p.id === selectedProcedureId);
            const config = procedure?.configs?.find((c: any) => c.locationId === selectedLocationId);
            return config?.id;
        },
        enabled: !!selectedProcedureId && !!selectedLocationId,
    });

    const { data: roomRateOverrides = [] } = useQuery({
        queryKey: ['room-rate-overrides', procedureConfigId],
        queryFn: () => roomTypesApi.getOverrides(procedureConfigId!),
        enabled: !!procedureConfigId,
    });

    const { data: icuRateOverrides = [] } = useQuery({
        queryKey: ['icu-rate-overrides', procedureConfigId],
        queryFn: () => icuRatesApi.getOverrides(procedureConfigId!),
        enabled: !!procedureConfigId,
    });

    const { data: pricing = [] } = useQuery({
        queryKey: ['pricing', procedureConfigId],
        queryFn: () => pricingApi.get(procedureConfigId!),
        enabled: !!procedureConfigId,
    });

    // Merge room types with overrides to show effective rates
    const roomTypesWithRates = useMemo(() => {
        return roomTypes.map((room: any) => {
            const override = roomRateOverrides.find((o: any) => o.roomTypeId === room.id);
            return {
                ...room,
                effectiveRate: override !== undefined ? override.overrideRate : room.defaultRate,
                hasOverride: override !== undefined,
            };
        });
    }, [roomTypes, roomRateOverrides]);

    // Merge ICU rates with overrides to show effective rates
    const icuRatesWithOverrides = useMemo(() => {
        return icuRates.map((icu: any) => {
            const override = icuRateOverrides.find((o: any) => o.icuRateId === icu.id);
            return {
                ...icu,
                effectiveRate: override !== undefined ? override.overrideRate : icu.dailyRate,
                hasOverride: override !== undefined,
            };
        });
    }, [icuRates, icuRateOverrides]);

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm<EstimateFormData>({
        resolver: zodResolver(estimateSchema),
        defaultValues: {
            stayDays: 1,
            icuDays: 0,
            selectedConditions: [],
            selectedItems: [],
            itemQuantities: {},
            doctors: [],
        },
    });

    const watchedValues = watch();

    const watchedDoctors = watch('doctors') || [];



    // Auto-calculate preview when form changes
    useEffect(() => {
        const calculatePreview = async () => {
            if (
                watchedValues.procedureConfigId &&
                watchedValues.stayDays &&
                watchedValues.roomType
            ) {
                setIsCalculating(true);
                try {
                    const result = await estimatesApi.calculate({
                        procedureConfigId: watchedValues.procedureConfigId,
                        stayDays: watchedValues.stayDays,
                        roomType: watchedValues.roomType,
                        icuDays: watchedValues.icuDays || 0,
                        icuRateId: watchedValues.icuRateId,
                        selectedConditions: watchedValues.selectedConditions,
                        selectedItems: watchedValues.selectedItems,
                        itemQuantities: watchedValues.itemQuantities,
                    });
                    setPreview(result);
                } catch (error) {
                    console.error('Failed to calculate preview:', error);
                    setPreview(null);
                } finally {
                    setIsCalculating(false);
                }
            }
        };

        const debounce = setTimeout(calculatePreview, 500);
        return () => clearTimeout(debounce);
    }, [
        watchedValues.procedureConfigId,
        watchedValues.stayDays,
        watchedValues.roomType,
        watchedValues.icuDays,
        watchedValues.icuRateId,
        watchedValues.selectedConditions,
        watchedValues.selectedItems,
        watchedValues.itemQuantities,
    ]);

    const createMutation = useMutation({
        mutationFn: estimatesApi.create,
        onSuccess: (data) => {
            navigate(`/estimates/${data.id}`);
        },
    });

    const onSubmit = (data: EstimateFormData) => {
        createMutation.mutate(data);
    };

    // Handle location change
    const handleLocationChange = (locationId: string) => {
        setSelectedLocationId(locationId);
        setValue('locationId', locationId);
        setValue('procedureConfigId', '');
        setSelectedProcedureId('');
        setValue('doctors', []);
        setPreview(null);
    };

    // Handle procedure change
    const handleProcedureChange = (procedureId: string) => {
        setSelectedProcedureId(procedureId);
        const procedure = procedures.find((p: any) => p.id === procedureId);
        if (procedure) {
            const config = procedure.configs?.find((c: any) => c.locationId === selectedLocationId);
            if (config) {
                setValue('procedureConfigId', config.id);
                setValue('stayDays', procedure.defaultStayDays || 1);
                setValue('icuDays', procedure.defaultIcuDays || 0);
                // Auto-select ICU rate if only one exists
                const activeIcuRates = icuRatesWithOverrides.filter((r: any) => r.isActive !== false);
                if (activeIcuRates.length === 1) {
                    setValue('icuRateId', activeIcuRates[0].id);
                }
            }
        }
    };

    // Merge sections with pricing data
    const sectionsWithPricing = useMemo(() => {
        if (!sections || sections.length === 0) return [];

        return sections.map((section: any) => {
            const itemsWithPricing = section.items?.map((item: any) => {
                const itemPricing = pricing.find((p: any) => p.costItemId === item.id);
                return {
                    ...item,
                    unitPrice: itemPricing?.unitPrice ?? item.defaultUnitPrice,
                    defaultQuantity: itemPricing?.defaultQuantity ?? null,
                    amount: itemPricing?.amount ?? item.defaultAmount,
                    percentage: itemPricing?.percentage ?? item.defaultPercentage,
                };
            });
            return {
                ...section,
                items: itemsWithPricing,
            };
        });
    }, [sections, pricing]);

    // Group preview sections
    const previewSections = preview?.sections?.reduce((acc: Record<string, any[]>, section: any) => {
        const sectionName = section.sectionName || section.name || 'Other';
        if (!acc[sectionName]) {
            acc[sectionName] = [];
        }
        acc[sectionName].push(...section.items);
        return acc;
    }, {}) || {};

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/estimates')}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Create New Estimate</h1>
                    <p className="text-slate-600">Fill in the details to generate a cost estimate</p>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Form Section */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Patient Information */}
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">Patient Information</h2>
                            </div>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Patient Name *</label>
                                    <input
                                        type="text"
                                        className={`input ${errors.patientName ? 'input-error' : ''}`}
                                        placeholder="John Doe"
                                        {...register('patientName')}
                                    />
                                    {errors.patientName && (
                                        <p className="error-text">{errors.patientName.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="label">Patient ID (optional)</label>
                                    <input
                                        type="text"
                                        className="input"
                                        placeholder="P12345"
                                        {...register('patientId')}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Procedure Selection */}
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">Procedure Details</h2>
                            </div>
                            <div className="card-body space-y-4">
                                <div>
                                    <label className="label">Location *</label>
                                    <select
                                        className={`input ${errors.locationId ? 'input-error' : ''}`}
                                        value={selectedLocationId}
                                        onChange={(e) => handleLocationChange(e.target.value)}
                                    >
                                        <option value="">Select location...</option>
                                        {locations.map((location: any) => (
                                            <option key={location.id} value={location.id}>
                                                {location.name}
                                            </option>
                                        ))}
                                    </select>
                                    {errors.locationId && (
                                        <p className="error-text">{errors.locationId.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="label">Procedure *</label>
                                    <select
                                        className={`input ${errors.procedureConfigId ? 'input-error' : ''}`}
                                        value={selectedProcedureId}
                                        onChange={(e) => handleProcedureChange(e.target.value)}
                                        disabled={!selectedLocationId || proceduresLoading}
                                    >
                                        <option value="">
                                            {!selectedLocationId
                                                ? 'Select location first...'
                                                : proceduresLoading
                                                    ? 'Loading procedures...'
                                                    : 'Select procedure...'}
                                        </option>
                                        {procedures
                                            .filter((p: any) =>
                                                p.configs?.some((c: any) => c.locationId === selectedLocationId && c.isActive)
                                            )
                                            .map((procedure: any) => (
                                                <option key={procedure.id} value={procedure.id}>
                                                    {procedure.code} - {procedure.name}
                                                </option>
                                            ))}
                                    </select>
                                    {errors.procedureConfigId && (
                                        <p className="error-text">{errors.procedureConfigId.message}</p>
                                    )}
                                    {selectedLocationId && !proceduresLoading && procedures.filter((p: any) =>
                                        p.configs?.some((c: any) => c.locationId === selectedLocationId && c.isActive)
                                    ).length === 0 && (
                                            <p className="text-xs text-warning-600 mt-1">
                                                ⚠️ No procedures configured for this location. Please add procedures in the admin panel.
                                            </p>
                                        )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">Room Type *</label>
                                        <select
                                            className={`input ${errors.roomType ? 'input-error' : ''}`}
                                            {...register('roomType')}
                                        >
                                            <option value="">Select room type...</option>
                                            {roomTypesWithRates.map((room: any) => (
                                                <option key={room.id} value={room.name}>
                                                    {room.name} - {formatCurrency(room.effectiveRate)}/day
                                                    {room.hasOverride && ' (Custom Rate)'}
                                                    {room.markupPercentage > 0 && ` (+${room.markupPercentage}% markup)`}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.roomType && (
                                            <p className="error-text">{errors.roomType.message}</p>
                                        )}
                                        {selectedLocationId && roomTypesWithRates.length === 0 && (
                                            <p className="text-xs text-warning-600 mt-1">
                                                No room types configured. Please add room types in the admin panel.
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="label">Hospital Stay (days) *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            className={`input ${errors.stayDays ? 'input-error' : ''}`}
                                            {...register('stayDays', { valueAsNumber: true })}
                                        />
                                        {errors.stayDays && (
                                            <p className="error-text">{errors.stayDays.message}</p>
                                        )}
                                    </div>
                                </div>

                                {/* ICU Days */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="label">ICU Days</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className={`input ${errors.icuDays ? 'input-error' : ''}`}
                                            {...register('icuDays', { valueAsNumber: true })}
                                        />
                                        {errors.icuDays && (
                                            <p className="error-text">{errors.icuDays.message}</p>
                                        )}
                                        <p className="text-xs text-slate-500 mt-1">
                                            Number of days in ICU (included in total stay)
                                        </p>
                                    </div>

                                    {(watchedValues.icuDays || 0) > 0 && icuRatesWithOverrides.length > 0 && (
                                        <div>
                                            <label className="label">ICU Type</label>
                                            <select
                                                className="input"
                                                {...register('icuRateId')}
                                            >
                                                <option value="">Select ICU type...</option>
                                                {icuRatesWithOverrides.filter((r: any) => r.isActive !== false).map((rate: any) => (
                                                    <option key={rate.id} value={rate.id}>
                                                        {rate.name} - {formatCurrency(rate.effectiveRate)}/day
                                                        {rate.hasOverride && ' (Custom Rate)'}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Doctor Selection */}
                        <div className="card">
                            <div className="card-header">
                                <h2 className="text-lg font-semibold">Doctors</h2>
                            </div>
                            <div className="card-body space-y-4">
                                <div>
                                    <select
                                        className={`input ${errors.doctors ? 'input-error' : ''}`}
                                        value={watchedDoctors[0]?.id || ''}
                                        onChange={(e) => {
                                            const doctorId = e.target.value;
                                            if (doctorId) {
                                                setValue('doctors', [{ id: doctorId, isPrimary: true }]);
                                            } else {
                                                setValue('doctors', []);
                                            }
                                        }}
                                        disabled={!selectedLocationId || doctors.length === 0}
                                    >
                                        <option value="">
                                            {!selectedLocationId
                                                ? 'Select location first...'
                                                : doctors.length === 0
                                                    ? 'No doctors available...'
                                                    : 'Select treating doctor...'}
                                        </option>
                                        {doctors.map((doctor: any) => (
                                            <option key={doctor.id} value={doctor.id}>
                                                {doctor.name} {doctor.qualification ? `(${doctor.qualification})` : ''}
                                            </option>
                                        ))}
                                    </select>

                                    {selectedLocationId && doctors.length === 0 && (
                                        <p className="text-xs text-warning-600 mt-1">
                                            ⚠️ No doctors available for this location. Please add doctors in the admin panel.
                                        </p>
                                    )}

                                    {doctors.length > 0 && (
                                        <p className="text-xs text-slate-500 mt-2">
                                            Select the primary treating doctor for this estimate.
                                        </p>
                                    )}

                                    {errors.doctors && (
                                        <p className="error-text">{errors.doctors.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Additional Options */}
                        {selectedLocationId && (
                            <div className="card">
                                <div className="card-header">
                                    <h2 className="text-lg font-semibold">Additional Options</h2>
                                </div>
                                <div className="card-body space-y-4">
                                    {/* Dynamically load percentage-based sections */}
                                    {sectionsLoading ? (
                                        <div className="space-y-2">
                                            {[...Array(2)].map((_, i) => (
                                                <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />
                                            ))}
                                        </div>
                                    ) : sectionsWithPricing
                                        .filter((section: any) => section.sectionType === 'PERCENTAGE')
                                        .map((section: any) => (
                                            <div key={section.id}>
                                                <label className="label">{section.name}</label>
                                                <div className="space-y-2">
                                                    {section.items?.map((item: any) => (
                                                        <label key={item.id} className="flex items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                value={item.id}
                                                                className="w-4 h-4"
                                                                {...register('selectedConditions')}
                                                            />
                                                            <span className="text-sm">{item.name}</span>
                                                        </label>
                                                    ))}
                                                    {(!section.items || section.items.length === 0) && (
                                                        <p className="text-xs text-slate-400">
                                                            No items configured for this section
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    {!sectionsLoading && sectionsWithPricing.filter((s: any) => s.sectionType === 'PERCENTAGE').length === 0 && (
                                        <p className="text-sm text-slate-400">
                                            No additional options available
                                        </p>
                                    )}

                                    {/* Selectable FIXED items (Pharmacy, Consumables, etc.) */}
                                    {sectionsWithPricing
                                        .filter((section: any) =>
                                            section.sectionType === 'FIXED' &&
                                            section.items?.some((item: any) => item.isSelectable && item.unit !== 'PER_UNIT')
                                        )
                                        .map((section: any) => (
                                            <div key={section.id} className="border-t border-slate-200 pt-4 mt-4">
                                                <label className="label">{section.name}</label>
                                                <div className="space-y-2">
                                                    {section.items
                                                        ?.filter((item: any) => item.isSelectable && item.unit !== 'PER_UNIT')
                                                        .map((item: any) => (
                                                            <label key={item.id} className="flex items-center gap-2">
                                                                <input
                                                                    type="checkbox"
                                                                    value={item.id}
                                                                    className="w-4 h-4"
                                                                    {...register('selectedItems')}
                                                                />
                                                                <span className="text-sm">{item.name}</span>
                                                            </label>
                                                        ))}
                                                </div>
                                            </div>
                                        ))}

                                    {sectionsWithPricing
                                        .filter((section: any) =>
                                            section.sectionType === 'FIXED' &&
                                            section.items?.some((item: any) => item.unit === 'PER_UNIT')
                                        )
                                        .map((section: any) => (
                                            <div key={`qty-${section.id}`} className="border-t border-slate-200 pt-4 mt-4">
                                                <label className="label">{section.name} (Variable)</label>
                                                <div className="space-y-4">
                                                    {section.items
                                                        ?.filter((item: any) => item.unit === 'PER_UNIT')
                                                        .map((item: any) => {
                                                            const isSelectable = item.isSelectable !== false;
                                                            const isSelected = !isSelectable || watchedValues.selectedItems?.includes(item.id);

                                                            // Find this item in the preview to get calculated price
                                                            const previewItem = preview?.sections
                                                                ?.flatMap((s: any) => s.items)
                                                                .find((i: any) => i.id === item.id);

                                                            // Use preview data if available, otherwise use pricing data
                                                            const displayUnitPrice = previewItem?.unitPrice ?? item.unitPrice ?? 0;
                                                            const displayQuantity = previewItem?.quantity ?? item.defaultQuantity ?? 1;
                                                            const displayLineTotal = previewItem?.lineTotal ?? (displayUnitPrice * displayQuantity);

                                                            return (
                                                                <div key={item.id} className="flex items-center gap-4 justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                                                                    <div className="flex items-center gap-4 flex-1">
                                                                        {isSelectable && (
                                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    value={item.id}
                                                                                    className="checkbox checkbox-sm checkbox-primary"
                                                                                    {...register('selectedItems')}
                                                                                />
                                                                                <span className="text-sm font-medium">{item.name}</span>
                                                                            </label>
                                                                        )}
                                                                        {!isSelectable && (
                                                                            <span className="text-sm font-medium">{item.name}</span>
                                                                        )}
                                                                    </div>

                                                                    <div className="flex items-center gap-6">
                                                                        {/* Unit Price Display */}
                                                                        <div className="text-right">
                                                                            <span className="block text-xs text-slate-500 uppercase">Rate</span>
                                                                            <span className="text-sm font-medium text-slate-700">
                                                                                {displayUnitPrice > 0 ? formatCurrency(displayUnitPrice) : '-'}
                                                                            </span>
                                                                        </div>

                                                                        {/* Quantity Input */}
                                                                        {isSelected && (
                                                                            <div>
                                                                                <label className="block text-xs text-slate-500 uppercase mb-1">Quantity</label>
                                                                                <div className="flex items-center gap-2">
                                                                                    <input
                                                                                        type="number"
                                                                                        min="1"
                                                                                        defaultValue={displayQuantity}
                                                                                        className="input input-sm w-20 text-center font-medium"
                                                                                        {...register(`itemQuantities.${item.id}`, { valueAsNumber: true })}
                                                                                    />
                                                                                    <span className="text-xs text-slate-500">units</span>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {!isSelected && (
                                                                            <div className="text-right min-w-[100px]">
                                                                                <span className="block text-xs text-slate-500 uppercase">Default Qty</span>
                                                                                <span className="text-sm font-medium text-slate-700">
                                                                                    {displayQuantity} units
                                                                                </span>
                                                                            </div>
                                                                        )}

                                                                        {/* Line Total Display */}
                                                                        {isSelected && (
                                                                            <div className="text-right min-w-[80px]">
                                                                                <span className="block text-xs text-slate-500 uppercase">Amount</span>
                                                                                <span className="text-sm font-bold text-primary-600">
                                                                                    {formatCurrency(displayLineTotal)}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Preview Section */}
                    <div className="lg:col-span-1">
                        <div className="card sticky top-6">
                            <div className="card-header flex items-center gap-2">
                                <Calculator className="w-5 h-5 text-primary-600" />
                                <h2 className="text-lg font-semibold">Cost Preview</h2>
                            </div>

                            <div className="card-body">
                                {isCalculating ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                                    </div>
                                ) : !preview ? (
                                    <div className="text-center py-8 text-slate-500">
                                        <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm">Fill in the form to see cost preview</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {/* Cost Breakdown */}
                                        {Object.entries(previewSections)
                                            .filter(([, items]) => (items as any[]).some((item) => item.lineTotal > 0))
                                            .map(([sectionName, items]) => (
                                            <div key={sectionName}>
                                                <h3 className="font-medium text-slate-700 text-sm mb-2">{sectionName}</h3>
                                                <div className="space-y-1">
                                                    {(items as any[]).filter((item) => item.lineTotal > 0).map((item, idx) => (
                                                        <div key={idx} className="flex justify-between text-sm">
                                                            <span className="text-slate-600">{item.itemName}</span>
                                                            <span className="font-medium">{formatCurrency(item.lineTotal)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Total */}
                                        <div className="pt-4 border-t-2 border-slate-200">
                                            <div className="flex justify-between items-center">
                                                <span className="font-semibold text-slate-900">Total Estimate</span>
                                                <span className="text-xl font-bold text-primary-600">
                                                    {formatCurrency(preview.total)}
                                                </span>
                                            </div>
                                            {preview.roomTypeMarkupPercent > 0 && (
                                                <p className="text-xs text-amber-600 mt-2">
                                                    * Includes {preview.roomTypeMarkupPercent}% room type premium (excludes room charges)
                                                </p>
                                            )}
                                        </div>

                                        {/* Disclaimer */}
                                        <div className="pt-4 border-t border-slate-200">
                                            <p className="text-xs text-slate-500 italic">
                                                This is an estimate only. Actual charges may vary based on clinical
                                                requirements.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="card-footer space-y-2">
                                <button
                                    type="submit"
                                    disabled={!preview || createMutation.isPending}
                                    className="btn btn-primary btn-lg w-full"
                                >
                                    {createMutation.isPending ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            Save Estimate
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate('/estimates')}
                                    className="btn btn-secondary btn-md w-full"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
